// ----------------------------------------------------------------------
// Materialização das edições de imagem (rotação, crop e desenho) em canvas offscreen.
// As transformações são "assadas" (baked) em um novo blob URL a cada aplicação de
// crop/rotação; os strokes ficam em coordenadas normalizadas (0..1) sobre a imagem
// atual e só são compostos no arquivo final no momento do envio.

export function loadImage(url) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Falha ao carregar imagem para edição'));
        img.src = url;
    });
}

function canvasToBlob(canvas, type, quality) {
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => (blob ? resolve(blob) : reject(new Error('Falha ao exportar imagem'))),
            type,
            quality
        );
    });
}

// Aplica rotação (graus) e/ou crop (croppedAreaPixels no espaço da imagem rotacionada)
// e retorna um novo object URL (PNG, sem perda, para edições intermediárias).
export async function bakeTransform(url, { rotation = 0, croppedAreaPixels = null } = {}) {
    const image = await loadImage(url);
    const rad = (rotation * Math.PI) / 180;
    const rotatedWidth =
        Math.abs(Math.cos(rad) * image.naturalWidth) + Math.abs(Math.sin(rad) * image.naturalHeight);
    const rotatedHeight =
        Math.abs(Math.sin(rad) * image.naturalWidth) + Math.abs(Math.cos(rad) * image.naturalHeight);

    const rotatedCanvas = document.createElement('canvas');
    rotatedCanvas.width = Math.round(rotatedWidth);
    rotatedCanvas.height = Math.round(rotatedHeight);
    const rctx = rotatedCanvas.getContext('2d');
    rctx.translate(rotatedCanvas.width / 2, rotatedCanvas.height / 2);
    rctx.rotate(rad);
    rctx.drawImage(image, -image.naturalWidth / 2, -image.naturalHeight / 2);

    let outCanvas = rotatedCanvas;
    if (croppedAreaPixels && croppedAreaPixels.width > 0 && croppedAreaPixels.height > 0) {
        outCanvas = document.createElement('canvas');
        outCanvas.width = Math.round(croppedAreaPixels.width);
        outCanvas.height = Math.round(croppedAreaPixels.height);
        const ctx = outCanvas.getContext('2d');
        ctx.drawImage(
            rotatedCanvas,
            croppedAreaPixels.x,
            croppedAreaPixels.y,
            croppedAreaPixels.width,
            croppedAreaPixels.height,
            0,
            0,
            outCanvas.width,
            outCanvas.height
        );
    }

    const blob = await canvasToBlob(outCanvas, 'image/png');
    return URL.createObjectURL(blob);
}

function drawStrokes(ctx, strokes, width, height) {
    for (const stroke of strokes) {
        if (!stroke?.points?.length) continue;
        ctx.strokeStyle = stroke.color;
        ctx.lineWidth = Math.max(1, (stroke.size || 0.006) * width);
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.beginPath();
        ctx.moveTo(stroke.points[0].x * width, stroke.points[0].y * height);
        for (let i = 1; i < stroke.points.length; i += 1) {
            ctx.lineTo(stroke.points[i].x * width, stroke.points[i].y * height);
        }
        ctx.stroke();
    }
}

// Compõe a imagem atual + strokes e devolve um File pronto para envio.
export async function composeFinalFile(url, strokes, originalFile) {
    const image = await loadImage(url);
    const canvas = document.createElement('canvas');
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(image, 0, 0);
    drawStrokes(ctx, strokes || [], canvas.width, canvas.height);

    const keepAlpha = ['image/png', 'image/webp', 'image/gif'].includes(originalFile?.type);
    const outType = keepAlpha ? 'image/png' : 'image/jpeg';
    const blob = await canvasToBlob(canvas, outType, 0.92);
    const baseName = (originalFile?.name || 'imagem').replace(/\.[^.]+$/, '');
    const ext = outType === 'image/png' ? 'png' : 'jpg';
    return new File([blob], `${baseName}.${ext}`, { type: outType });
}

export function hasImageEdits(item) {
    return Boolean(item?.editedUrl || (item?.strokes && item.strokes.length > 0));
}
